'use strict';

import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import parse from 'html-react-parser';
import L, {
    DomEvent
} from 'leaflet';
import './Leaflet.SmoothWheelZoom';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import {Tooltip} from 'primereact/tooltip';

import {__} from '~/shared/src/utilities/messageCatalog';
import ItemType from '~/shared/src/mm/ItemType';
import {
    LoadConfigParameters,
    LoadConfigResult,
    LoadDataParameters
} from '~/shared/src/utilities/modules/map';
import {LayerType} from '~/shared/src/utilities/map';
import {
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import {MapDataSet} from '~/shared/src/utilities/mapDataSet';
import Item from '~/shared/src/ci/Item';
import Reference from '~/shared/src/ci/Reference';
import messageUtilities from '~/shared/src/utilities/messageUtils';
import dataUtilities from '~/shared/src/utilities/dataUtils';
import {valuePrecision} from '~/shared/src/utilities/modules/finder';
import {convertToDate} from '~/shared/src/utilities/valueConverters';

import {AllProps as FormAllProps} from '~/frontend/src/components/base/Form';
import xhr from '~/frontend/src/utilities/xhr';
import {
    getPanelProps,
    getSubPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    getValueDisplayName,
    getIconStringSpan
} from '~/frontend/src/utilities/renderHelpers';
import {
    isInvalidHTML,
    renderValue,
    resolveStyle
} from '~/frontend/src/utilities/valueRenderers';
import {getColorFromColorPalette} from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';
import {
    getItemForDataDescriptorFromRowData,
    getReferenceForDataDescriptorFromRowData,
    getValueForDataDescriptorFromRowData
} from '~/frontend/src/utilities/dataUtils';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import loadData from '~/frontend/src/actions/data/loadData';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';
import openPanelForItemType from '~/frontend/src/actions/panelManager/openPanelForItemType';

type ResolvedDataObjects = {
    [ dataObjectId : string ] : {
        item : Item;
        reference? : Reference;
    }
};
export type Marker = {
    id : string;
    rootId : string;
    position : [ number, number ];
    icon? : L.DivIconOptions['html'];
    tooltipContent? : string;
    item? : {
        schema : Schema;
        itemTypeName : string;
        id : number;
    };
    timestamp? : string;
    connectTo? : [ number, number ][];
};
export type MarkerGroup = {
    dataSetId : number;
    id : string;
    label : string;
    color : string;
    markers : Marker[];
    drawConnectionsByConnect : boolean;
    drawConnectionsByTime : boolean;
    connectionStyle? : L.PolylineOptions;
};

const connectionStyleKeys = keys<L.PolylineOptions>();

type Data = {
    [ dataDescriptorAlias : string ] : any;
};
type StringData = {
    [ dataDescriptorAlias : string ] : string
};
type MapDataSetMetaData = {
    [ mapDataSetKey : string ] : {
        rowData? : LoadMetaDataRowData[];
        rowDataMapping? : LoadMetaDataRowDataMapping;
    };
};


export type DefaultProps = {
    schema? : Schema;
    layerType? : LayerType;
    showTooltip? : boolean;
    center? : [ number, number ];
    zoom? : number;
    fitBounds? : boolean;
    initialized? : boolean;
    markerIconClassName? : string;
};
export const defaultProps : Required<DefaultProps> = {
    schema             : 'DATA',
    layerType          : 'standard',
    showTooltip        : true,
    center             : [ 51.163361, 10.447683 ],
    zoom               : 6,
    fitBounds          : true,
    initialized        : false,
    markerIconClassName: 'yn-marker'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    formUid? : string;

    /**
     * The key of the overview config.
     */
    viewKey : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    dataSets? : MapDataSet[];

    externalMarkers? : MarkerGroup[];

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadConfigResult[ 'data' ]} config
     */
    onConfigLoad? : ( config : LoadConfigResult[ 'data' ] ) => void;

    /**
     * Function invoked if data was successfully loaded.
     */
    onDataLoad? : () => void;

    /**
     * Additional parameters which have to be relayed to the CFG:Query used to load the results.
     */
    additionalQueryParameters? : Required<LoadDataParameters[ 'parameters' ]>;

    onSetCoordinateByClick? : ( coordinate : [ number, number ] ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;
const setFormParam = origSetParam as <K extends keyof FormAllProps>( panelKey : string, paramKey : K, paramValue : FormAllProps[K] ) => SetParamAction;


export type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    configLoaded? : boolean;
    dataLoaded? : boolean;
    metaData? : PanelElusiveDataState[ 'metaData' ] & {
        mapDataSetMetaData? : MapDataSetMetaData;
    };
    overlays? : {
        [ name : string ] : boolean;
    };
};


export type ReferenceType = {
    fitBounds : () => void;
};


/**
 * Builds a marker object based on the provided dataset, data, and configuration.
 *
 * @param {MapDataSet} dataSet - The dataset configuration containing location, time, and style descriptors.
 * @param {Data} data - The data object containing the actual values for generating the marker.
 * @param {StringData} stringData - The string data mappings for resolving textual information.
 * @param {number} dataSetIndex - The index of the dataset in the list of datasets.
 * @param {number} markerIndex - The index of the marker within the current dataset.
 * @param {string} color - The base color to use for the marker's appearance.
 * @param {ResolvedDataObjects} [resolvedDataObjects] - Optional pre-resolved data objects for faster lookup.
 * @return {Marker|undefined} Returns a newly created marker object or undefined if the required properties are missing.
 */
const buildMarker = ( dataSet : MapDataSet, data : Data, stringData : StringData, dataSetIndex : number, markerIndex : number, color : string, resolvedDataObjects? : ResolvedDataObjects ) => {
    let retVal : Marker | undefined = undefined;

    if ( dataSet.locationDataDescriptor && data[ dataSet.locationDataDescriptor.alias ] ) {
        const id       = `marker_${dataSetIndex}_${markerIndex}`,
              item     = dataSet.rootDataDescriptor?.dataObject?.id ?
                  resolvedDataObjects?.[ dataSet.rootDataDescriptor.dataObject.id ]?.item
                  : undefined,
              position = Array.isArray( data[ dataSet.locationDataDescriptor.alias ] )
                  ? data[ dataSet.locationDataDescriptor.alias ]
                  : typeof data[ dataSet.locationDataDescriptor.alias ] === 'string'
                      ? data[ dataSet.locationDataDescriptor.alias ].split( ',' )
                      : undefined;

        if ( position ) {
            let timestamp : string | undefined,
                tooltip = '',
                icon    = '';

            // process timestamp
            if ( dataSet.timeDataDescriptor?.alias ) {
                if ( typeof data[ dataSet.timeDataDescriptor.alias ] === 'string' ) {
                    timestamp = data[ dataSet.timeDataDescriptor.alias ];
                }
                else if ( data[ dataSet.timeDataDescriptor.alias ]?.toISOString ) {
                    timestamp = data[ dataSet.timeDataDescriptor.alias ].toISOString();
                }
                else {
                    timestamp = stringData[ dataSet.timeDataDescriptor.alias ];
                }
            }

            // process messages
            if ( dataSet.messages?.tooltip ) {
                if ( tooltip !== '' ) {
                    tooltip += '<br/><br/>';
                }

                if ( typeof dataSet.messages.tooltip !== 'string' ) {
                    if ( item ) {
                        const labeledResolvedDataObjects : ResolvedDataObjects = {};

                        if ( resolvedDataObjects ) {
                            for ( const label of Object.keys( dataSet.messages.tooltip.dataObjects ) ) {
                                if ( resolvedDataObjects[ dataSet.messages.tooltip.dataObjects[ label ].id ] ) {
                                    labeledResolvedDataObjects[ label ] = resolvedDataObjects[ dataSet.messages.tooltip.dataObjects[ label ].id ];
                                }
                            }
                        }

                        tooltip += messageUtilities.getMessageTemplateStringResolverForResolvedDataObjects( item, undefined, labeledResolvedDataObjects )( __( dataSet.messages.tooltip.key ) );
                    }
                    else {
                        tooltip += __( dataSet.messages.tooltip.key, stringData );
                    }
                }
                else {
                    tooltip += __( dataSet.messages.tooltip );
                }
            }
            else if ( item ) {
                tooltip += `<strong>${item.displayAttributeValue}</strong>`;
            }

            // process style
            if ( dataSet.styles?.style?.length ) {
                const style = resolveStyle( dataSet.styles.style, data );

                if ( style[ 'color' ] ) {
                    color = style[ 'color' ];
                }
            }

            // process icon
            if ( dataSet.images?.icon ) {
                icon = `<span><span class="yn-marker-border" style="background-color: ${color};"></span>${getIconStringSpan( `yn-marker-icon ${dataSet.images?.icon}`, { id, style: `color: ${color};`, 'data-color': color } )}</span>`;
            }
            else if ( item ) {
                icon = `<span><span class="yn-marker-border" style="background-color: ${color};"></span>${getIconStringSpan( `yn-marker-icon ${item.itemType.icon}`, { id, style: `color: ${color};` } )}</span>`;
            }
            else {
                icon = `<span><span class="yn-marker-border" style="background-color: ${color};"></span><span id="${id}" class="yn-marker-icon material-symbols-outlined material-symbols-fill" style="color: ${color}">location_on</span></span>`;
            }

            retVal = {
                id,
                rootId        : item
                    ? String( item.id )
                    : dataSet.rootDataDescriptor?.alias && stringData[ dataSet.rootDataDescriptor.alias ]
                        ? stringData[ dataSet.rootDataDescriptor.alias ]
                        : id,
                position,
                icon,
                tooltipContent: tooltip,
                item          : item
                    ? {
                        id          : item.id,
                        schema      : item.schema,
                        itemTypeName: item.itemType.name
                    }
                    : undefined,
                timestamp
            };
        }
    }

    return retVal;
};
/**
 * Builds a marker group based on the provided dataset and configuration.
 *
 * @param {MapDataSet} dataSet - The dataset containing the data and metadata for the markers.
 * @param {number} dataSetIndex - The index of the dataset being processed.
 * @param {string} dataContext - The context used to resolve data values.
 * @param {string} language - The language to be used for labels and other localized content.
 * @param {LoadMetaDataRowData[]} [dataSetRowData] - Optional row data for the dataset.
 * @param {LoadMetaDataRowDataMapping} [dataSetRowDataMapping] - Optional mappings for row data.
 * @param {boolean} [groupOnly=false] - A flag indicating whether only the group structure should be created without markers.
 *
 * @return {MarkerGroup} The constructed marker group object containing markers and metadata.
 */
export const buildMarkerGroup = ( dataSet : MapDataSet, dataSetIndex : number, dataContext : string, language : string, dataSetRowData? : LoadMetaDataRowData[], dataSetRowDataMapping? : LoadMetaDataRowDataMapping, groupOnly : boolean = false ) => {
    let label          = dataSet.data.key,
        color : string = getColorFromColorPalette( dataSetIndex ),
        connectionStyle : L.PolylineOptions | undefined;

    if ( dataSet.messages?.label ) {
        label = __( typeof dataSet.messages.label === 'string' ? dataSet.messages.label : dataSet.messages.label.key );
    }
    else if ( dataSet.rootDataDescriptor && dataSet.rootDataDescriptor.type !== 'static' && dataSet.rootDataDescriptor.type !== 'expression' && dataSet.query.dataStructure ) {
        const itemType = dataUtilities.getMetaModelData(
            dataSet.rootDataDescriptor,
            dataSet.query.dataStructure,
            ( dataSet.rootDataDescriptor.dataObject.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema
        )?.[ 0 ]?.object as ItemType;

        if ( itemType ) {
            label = itemType.label;
        }
    }

    // process style
    if ( dataSet.styles?.style?.length ) {
        const style = resolveStyle( dataSet.styles.style, {} );

        if ( style[ 'color' ] ) {
            color = style[ 'color' ];
        }
    }

    // process connection style
    if ( dataSet.styles?.connectionStyle?.length ) {
        connectionStyle = _.pick( resolveStyle( dataSet.styles.connectionStyle, {} ), connectionStyleKeys ) as unknown as L.PolylineOptions;
    }

    const retVal : MarkerGroup = {
        id                      : `markerGroup_${dataSetIndex}`,
        dataSetId               : dataSet.data.id,
        label,
        color,
        markers                 : [],
        drawConnectionsByConnect: Boolean( dataSet.connectToDataDescriptor ),
        drawConnectionsByTime   : Boolean( dataSet.timeDataDescriptor ),
        connectionStyle
    };

    if ( !groupOnly ) {
        const stringifiedMarkers : string[] = [];

        let markerCount = 0;

        if ( dataSetRowData ) {
            for ( let rowDataRawIndex = 0; rowDataRawIndex < dataSetRowData.length; rowDataRawIndex++ ) {
                const rowData                                   = dataSetRowData[ rowDataRawIndex ] as LoadMetaDataRowData,
                      data : Data                               = {},
                      stringData : StringData                   = {},
                      resolvedDataObjects : ResolvedDataObjects = {};

                // get data and stringData by DataDescriptors
                for ( const dataDescriptor of dataSet.dataDescriptors ?? [] ) {
                    if ( dataSet.query.type === 'DataStructure' ) {
                        const value = getValueForDataDescriptorFromRowData(
                            dataContext,
                            dataDescriptor,
                            rowData,
                            dataSet.query.dataStructure
                        );

                        if ( dataDescriptor.dataObject?.id ) {
                            const item = getItemForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

                            if ( item ) {
                                resolvedDataObjects[ dataDescriptor.dataObject.id ] = {
                                    item,
                                    reference: getReferenceForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData )
                                };
                            }
                        }

                        if ( value ) {
                            data[ dataDescriptor.alias ] = value.value;

                            // enum
                            if ( Array.isArray( value.value ) ) {
                                stringData[ dataDescriptor.alias ] = value.value.join( ', ' );
                            }

                            // not empty
                            else if ( value.value !== undefined && value.value !== null && String( value.value ).trim() !== '' ) {
                                // ValueSet
                                if ( value.valueSetId ) {
                                    stringData[ dataDescriptor.alias ] = getValueDisplayName( value.valueSetId, value.value, dataSet.query.schema );
                                }

                                // normal
                                else {
                                    const renderValueParams : any = {
                                        schema   : dataSet.query.schema,
                                        language,
                                        valueUnit: value.valueUnit,
                                        valuePrecision
                                    };

                                    stringData[ dataDescriptor.alias ] = renderValue( value.valueType, value.value, renderValueParams, false, true ) as string;
                                }
                            }
                            else {
                                stringData[ dataDescriptor.alias ] = '';
                            }
                        }
                    }

                    if ( !data[ dataDescriptor.alias ] ) {
                        data[ dataDescriptor.alias ] = rowData[ dataSetRowDataMapping?.[ dataDescriptor.alias ] ?? '' ]
                            ?? rowData[ dataDescriptor.alias ];

                        stringData[ dataDescriptor.alias ] = String( data[ dataDescriptor.alias ] );
                    }
                }

                // get missing data and stringData from rowData
                for ( const key of Object.keys( rowData ) ) {
                    if ( data[ key ] === undefined ) {
                        data[ key ] = rowData[ key ];
                    }
                    if ( stringData[ key ] === undefined ) {
                        stringData[ key ] = String( rowData[ key ] );
                    }
                }

                let marker = buildMarker( dataSet, data, stringData, dataSetIndex, markerCount, color, resolvedDataObjects );

                if ( marker ) {
                    const stringifiedMarker = JSON.stringify( marker ).replaceAll( marker.id, 'replaced' ),
                          index             = stringifiedMarkers.indexOf( stringifiedMarker );

                    if ( index === -1 ) {
                        stringifiedMarkers.push( stringifiedMarker );
                        markerCount++;
                        retVal.markers.push( marker );
                    }
                    else {
                        marker = retVal.markers[ index ];
                    }

                    if ( marker && dataSet.connectToDataDescriptor?.alias ) {
                        const connectToPosition = Array.isArray( data[ dataSet.connectToDataDescriptor.alias ] )
                            ? data[ dataSet.connectToDataDescriptor.alias ]
                            : typeof data[ dataSet.connectToDataDescriptor.alias ] === 'string'
                                ? data[ dataSet.connectToDataDescriptor.alias ].split( ',' )
                                : undefined;

                        if ( connectToPosition ) {
                            marker.connectTo = ( [] as [ number, number ][] ).concat( marker.connectTo ?? [] ).concat( [ connectToPosition ] );
                        }
                    }
                }
            }
        }
    }

    return retVal;
};


const Map = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch          = useDispatch(),
          subPanelProps                   = useSelector( ( state : StoreState ) => {
              return getSubPanelProps( props.uid, state );
          } ),
          formPersistentSubPanelPropsOrig = useSelector( ( state : StoreState ) => {
              if ( props.formUid ) {
                  return ( getPanelProps( props.formUid, state )?.panelProps ?? getSubPanelProps( props.formUid, state ) )?.persistentSubPanelProps;
              }
              else {
                  return undefined;
              }
          } ),
          formPersistentSubPanelProps     = useRef<any | undefined>( undefined );

    formPersistentSubPanelProps.current = formPersistentSubPanelPropsOrig;

    const setFormPersistentSubPanelProps = useCallback(
        <K extends keyof AllProps>( paramKey : K, paramValue : AllProps[K] ) => {
            if ( props.formUid && formPersistentSubPanelProps.current?.[ props.uid ]?.[ paramKey ] !== paramValue ) {
                formPersistentSubPanelProps.current = {
                    ...formPersistentSubPanelProps.current,
                    [ props.uid ]: {
                        ...formPersistentSubPanelProps.current?.[ props.uid ],
                        [ paramKey ]: paramValue
                    }
                };

                dispatch( setFormParam(
                    props.formUid,
                    'persistentSubPanelProps',
                    formPersistentSubPanelProps.current
                ) );
            }
        },
        [
            props.formUid,
            props.uid
        ]
    );

    props = useMemo(
        () => {
            return {
                ...props,
                ...subPanelProps,
                ...formPersistentSubPanelPropsOrig?.[ props.uid ]
            };
        },
        [
            props,
            subPanelProps,
            formPersistentSubPanelPropsOrig?.[ props.uid ]
        ]
    );

    const {
              additionalQueryParameters,
              center,
              configHash,
              dataSets,
              externalMarkers,
              fitBounds,
              initialized,
              layerType,
              markerIconClassName,
              onConfigLoad,
              onDataLoad,
              onSetCoordinateByClick,
              schema,
              showTooltip,
              uid,
              viewKey,
              zoom
          }                                       = props,
          dataContext                             = `${props.dataContext}_map`,
          elementId                               = `${uid}_map`,
          user                                    = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          elusiveData                             = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          dataElusiveData                         = useSelector( ( state : StoreState ) => state.elusiveData?.[ dataContext ] as ElusiveData | undefined ),
          isLoading                               = useRef<boolean>( false ),
          hasRendered                             = useRef<boolean>( false ),
          map                                     = useRef<L.Map>(),
          layerControl                            = useRef<L.Control.Layers>(),
          markersLayers                           = useRef<L.LayerGroup[]>(),
          markers                                 = useRef<MarkerGroup[]>(),
          zoomTimeoutId                           = useRef<NodeJS.Timeout | undefined>(),
          controller                              = useRef<AbortController>( new AbortController() ),
          fullScreen                              = useRef<boolean>( false ),
          rerenderTriggerCurrent                  = useRef<number>( 0 ),
          [ tooltipContent, setTooltipContent ]   = useState<React.ReactNode>( '' ),
          [ tooltipTarget, setTooltipTarget ]     = useState<string>( '' ),
          [ rerenderTrigger, setRerenderTrigger ] = useState<number>( 0 ),
          configRequestUid                        = 'loadConfig',
          dataRequestUid                          = 'loadData',
          isConfigLoaded                          = useMemo(
              () => {
                  return elusiveData?.configLoaded ?? false;
              },
              [
                  elusiveData?.configLoaded ?? false
              ]
          ),
          isDataLoaded                            = useMemo(
              () => {
                  return elusiveData?.dataLoaded;
              },
              [
                  elusiveData?.dataLoaded ?? false
              ]
          ),
          language                                = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                      = useMemo(
              () => {
                  return JSON.stringify( dataElusiveData?.data ?? {} );
              },
              [
                  dataElusiveData?.data
              ]
          ),
          mapDataSetMetaData                      = useMemo(
              () => {
                  return elusiveData?.metaData?.mapDataSetMetaData;
              },
              [
                  elusiveData?.metaData?.mapDataSetMetaData
              ]
          ),
          overlays                                = useMemo(
              () => {
                  return elusiveData?.overlays;
              },
              [
                  elusiveData?.overlays
              ]
          ),
          layers                                  = useMemo(
              () => {
                  const standard  = L.tileLayer( 'https://tile.openstreetmap.de/{z}/{x}/{y}.png', {
                            maxZoom    : 18,
                            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        } ),
                        opnv      = L.tileLayer( 'https://tileserver.memomaps.de/tilegen/{z}/{x}/{y}.png', {
                            maxZoom    : 18,
                            attribution: 'Map <a href="https://memomaps.de/">memomaps.de</a> <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        } ),
                        satellite = L.layerGroup( [
                            L.tileLayer( 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                                attribution: 'Tiles © Esri'
                            } ),
                            L.tileLayer( 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}', {
                                attribution: '© Esri',
                                opacity    : 0.4
                            } ),
                            L.tileLayer( 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
                                attribution: '© Esri',
                                opacity    : 0.9
                            } )
                        ] ),
                        topo      = L.tileLayer( 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
                            maxZoom    : 17,
                            attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
                        } );

                  return {
                      standard,
                      opnv,
                      satellite,
                      topo
                  };
              },
              [
                  'onlyOnce'
              ]
          ),
          maps                                    = useMemo(
              () => {
                  const retVal : { [ label : string ] : L.TileLayer | L.LayerGroup<any> } = {};

                  for ( const type of Object.keys( layers ) ) {
                      retVal[ __( `YN.base.frontend.map.layer.${type}.label` ) ] = layers[ type ];
                  }

                  return retVal
              },
              [
                  language,
                  layers
              ]
          ),
          loadConfig                              = useCallback(
              async () => {
                  isLoading.current = true;

                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  try {
                      const params : LoadConfigParameters = {
                                viewKey,
                                parameters: additionalQueryParameters
                            },
                            response                      = await xhr.post(
                                `${BASE_URL}/api/map/loadConfig`,
                                params,
                                undefined,
                                uid,
                                configRequestUid
                            );

                      if ( response?.data?.success ) {
                          const result = response.data as LoadConfigResult,
                                config = result.data;

                          dispatch( setParams( uid, {
                              configHash: config.configHash,
                              schema    : config.schema,
                              fitBounds : config.fitBounds,
                              layerType : config.layerType,
                              dataSets  : config.dataSets
                          } ) );
                          dispatch( setElusiveData( uid, { configLoaded: true } ) );

                          onConfigLoad?.( config );
                      }
                  }
                  finally {
                      // always end the masking request, even if loading/processing threw
                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;
                  }
              },
              [
                  additionalQueryParameters,
                  uid,
                  viewKey
              ]
          ),
          load                                    = useCallback(
              async () => {
                  isLoading.current = true;

                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  try {
                      if ( global.data[ dataContext ] ) {
                          delete global.data[ dataContext ];
                      }

                      const params : LoadDataParameters = {
                                viewKey,
                                configHash,
                                parameters: additionalQueryParameters
                            },
                            response                    = await dispatch(
                                loadData(
                                    'map/loadData',
                                    dataContext,
                                    uid,
                                    dataRequestUid,
                                    schema,
                                    {
                                        params,
                                        mergeMode        : 'override',
                                        extraMetaDataKeys: [ 'mapDataSetMetaData' ]
                                    }
                                )
                            );

                      if ( response ) {
                          onDataLoad?.();
                      }

                      dispatch( setElusiveData( uid, { dataLoaded: true } ) );
                  }
                  finally {
                      // always end the masking request, even if loading/processing threw
                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;
                  }
              },
              [
                  additionalQueryParameters,
                  configHash,
                  dataContext,
                  schema,
                  uid,
                  viewKey
              ]
          ),
          mapElement                              = useMemo(
              () => {
                  return <div
                      key={elementId}
                      id={elementId}
                      className="yn-map"
                      style={{
                          height: '100%',
                          width : '100%'
                      }}
                  ></div>;
              },
              [
                  elementId
              ]
          ),
          tooltip                                 = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( showTooltip && tooltipTarget && tooltipContent ) {
                      retVal = <Tooltip
                          key={`${elementId}_tooltip`}
                          id={`${elementId}_tooltip`}
                          target={tooltipTarget}
                          showEvent="onmouseover"
                          showDelay={500}
                          mouseTrack={true}
                      >
                          {tooltipContent}
                      </Tooltip>;
                  }

                  return retVal;
              },
              [
                  elementId,
                  initialized,
                  showTooltip,
                  tooltipContent,
                  tooltipTarget
              ]
          ),
          onFullscreenChange                      = useCallback(
              () => {
                  const fullscreenButtonElem = document.getElementById( `${elementId}_zoomCtrl_fullscreen` );

                  if ( document.fullscreenElement ) {
                      fullScreen.current = true;

                      if ( fullscreenButtonElem ) {
                          fullscreenButtonElem.innerHTML = 'fullscreen_exit';
                          fullscreenButtonElem.title = 'Exit fullscreen';
                          fullscreenButtonElem.setAttribute( 'aria-label', 'Exit fullscreen' );
                      }
                  }
                  else {
                      fullScreen.current = false;

                      if ( fullscreenButtonElem ) {
                          fullscreenButtonElem.innerHTML = 'fullscreen';
                          fullscreenButtonElem.title = 'Fullscreen';
                          fullscreenButtonElem.setAttribute( 'aria-label', 'Fullscreen' );
                      }

                      window.setTimeout(
                          () => {
                              setRerenderTrigger( rerenderTriggerCurrent.current + 1 );
                          },
                          10
                      );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onMarkerOver                            = useCallback(
              ( marker : Marker, event : any ) => {
                  let tC : string | React.ReactNode | undefined = marker.tooltipContent;

                  if ( tC && !isInvalidHTML( tC as string ) ) {
                      tC = parse( tC as string );
                  }

                  setTooltipContent( tC );
                  setTooltipTarget( `#${marker.id}` );
              },
              [
                  'onlyOnce'
              ]
          ),
          onMarkerOut                             = useCallback(
              () => {
                  setTooltipContent( '' );
                  setTooltipTarget( '' );
              },
              [
                  'onlyOnce'
              ]
          ),
          onMarkerDblClick                        = useCallback(
              ( marker : Marker, event : any ) => {
                  if ( marker.item ) {
                      dispatch( openPanelForItemType(
                          marker.item.schema,
                          marker.item.itemTypeName,
                          {
                              schema    : marker.item.schema,
                              rootItemId: marker.item.id
                          },
                          undefined,
                          event,
                          undefined,
                          [ 'FinderOverview', 'Overview', 'XmlImport' ]
                      ) );
                  }

                  event.stopPropagation();
                  event.preventDefault();
              },
              [
                  'onlyOnce'
              ]
          ),
          onZoomEnd                               = useCallback(
              ( event : any ) => {
                  addEventsToMarkers();

                  if ( setFormPersistentSubPanelProps ) {
                      setFormPersistentSubPanelProps( 'zoom', event.target.getZoom() );
                      setFormPersistentSubPanelProps( 'center', event.target.getCenter() );
                  }

                  dispatch( setParam( uid, 'zoom', event.target.getZoom() ) );
                  dispatch( setParam( uid, 'center', event.target.getCenter() ) );
              },
              [
                  'onlyOnce'
              ]
          ),
          onMoveEnd                               = useCallback(
              ( event : any ) => {
                  addEventsToMarkers();

                  if ( setFormPersistentSubPanelProps ) {
                      setFormPersistentSubPanelProps( 'zoom', event.target.getZoom() );
                      setFormPersistentSubPanelProps( 'center', event.target.getCenter() );
                  }

                  dispatch( setParam( uid, 'zoom', event.target.getZoom() ) );
                  dispatch( setParam( uid, 'center', event.target.getCenter() ) );
              },
              [
                  'onlyOnce'
              ]
          ),
          onBaseLayerChange                       = useCallback(
              ( event : any ) => {
                  let layerTypeToSet : Required<DefaultProps>[ 'layerType' ] = 'standard';

                  switch ( event.name ) {
                      case __( 'YN.base.frontend.map.layer.opnv.label' ):
                          layerTypeToSet = 'opnv';
                          break;
                      case __( 'YN.base.frontend.map.layer.satellite.label' ):
                          layerTypeToSet = 'satellite';
                          break;
                      case __( 'YN.base.frontend.map.layer.topo.label' ):
                          layerTypeToSet = 'topo';
                          break;
                  }

                  if ( setFormPersistentSubPanelProps ) {
                      setFormPersistentSubPanelProps( 'layerType', layerTypeToSet );
                  }

                  dispatch( setParam( uid, 'layerType', layerTypeToSet ) );
              },
              [
                  'onlyOnce'
              ]
          ),
          onOverlayAdd                            = useCallback(
              ( event : any ) => {
                  const overlaysToSet = { ...overlays };

                  addEventsToMarkers();

                  overlaysToSet[ event.name ] = true;

                  dispatch( setElusiveData( uid, { overlays: overlaysToSet } ) );
              },
              [
                  'onlyOnce'
              ]
          ),
          onOverlayRemove                         = useCallback(
              ( event : any ) => {
                  const overlaysToSet = { ...overlays };

                  addEventsToMarkers();

                  overlaysToSet[ event.name ] = false;

                  dispatch( setElusiveData( uid, { overlays: overlaysToSet } ) );
              },
              [
                  'onlyOnce'
              ]
          ),
          onMapClick                              = useCallback(
              ( event : L.LeafletMouseEvent ) => {
                  onSetCoordinateByClick?.( [ event.latlng.lat, event.latlng.lng ] );
              },
              [
                  'onlyOnce'
              ]
          ),
          addEventsToMarkers                      = useCallback(
              () => {
                  if ( zoomTimeoutId.current ) {
                      clearTimeout( zoomTimeoutId.current );
                  }

                  zoomTimeoutId.current = setTimeout(
                      () => {
                          controller.current.abort();
                          controller.current = new AbortController();

                          for ( const markerGroup of markers.current ?? [] ) {
                              for ( const marker of markerGroup.markers ) {
                                  const markerEl = document.getElementById( marker.id );

                                  if ( markerEl ) {
                                      markerEl.addEventListener( 'dblclick', onMarkerDblClick.bind( null, marker ), { signal: controller.current.signal } );
                                      markerEl.addEventListener( 'mouseover', onMarkerOver.bind( null, marker ), { signal: controller.current.signal } );
                                      markerEl.addEventListener( 'mouseout', onMarkerOut, { signal: controller.current.signal } );
                                  }
                              }
                          }
                      },
                      25
                  );
              },
              [
                  'onlyOnce'
              ]
          ),
          doFitBounds                             = () => {
              if ( map.current ) {
                  let bounds : L.LatLngBounds | undefined;

                  for ( const markerGroup of markers.current ?? [] ) {
                      for ( const marker of markerGroup.markers ) {
                          if ( !bounds ) {
                              bounds = L.latLngBounds( marker.position, marker.position );
                          }
                          else {
                              bounds.extend( marker.position );
                          }
                      }
                  }

                  if ( bounds ) {
                      map.current.fitBounds( bounds );
                  }
              }
          };

    rerenderTriggerCurrent.current = rerenderTrigger;
    markers.current = useMemo(
        () => {
            let retVal : MarkerGroup[] = [];

            if ( externalMarkers ) {
                retVal = externalMarkers;
            }
            else {
                for ( let dataSetIndex = 0; dataSetIndex < ( dataSets ?? [] ).length; dataSetIndex++ ) {
                    const dataSet               = dataSets![ dataSetIndex ],
                          dataSetRowData        = mapDataSetMetaData?.[ dataSet.data.key ].rowData,
                          dataSetRowDataMapping = mapDataSetMetaData?.[ dataSet.data.key ].rowDataMapping;

                    retVal.push( buildMarkerGroup( dataSet, dataSetIndex, dataContext, language, dataSetRowData, dataSetRowDataMapping ) );
                }
            }

            return retVal;
        },
        [
            dataContext,
            dataSets,
            externalMarkers,
            globalDataVersions,
            mapDataSetMetaData,
            language
        ]
    );

    // expose via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            fitBounds: doFitBounds
        } )
    );

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    // remove observed element from resizeObserver
    useEffect(
        () => {
            document.addEventListener( 'fullscreenchange', onFullscreenChange );

            // unmount
            return () => {
                document.removeEventListener( 'fullscreenchange', onFullscreenChange );

                if ( markersLayers.current ) {
                    for ( const layer of markersLayers.current ) {
                        layerControl.current?.removeLayer( layer );
                        map.current?.removeLayer( layer );
                    }
                }

                map.current?.remove();
                map.current = undefined;
                markers.current = [];
                markersLayers.current = [];

                dispatch( cancelRequests( uid ) );
            }
        },
        []
    );

    // ( on update )
    // load config and data if / initialize map
    useEffect(
        () => {
            if ( viewKey ) {
                const wrapperEl = document.getElementById( elementId );

                // load config and data if
                if ( !isLoading.current && externalMarkers === undefined ) {
                    if ( !isConfigLoaded ) {
                        void loadConfig();
                    }
                    else if ( !isDataLoaded ) {
                        void load();
                    }
                }

                // initialize map
                if ( wrapperEl && ( isConfigLoaded || externalMarkers !== undefined ) && !map.current ) {
                    hasRendered.current = true;

                    map.current = L.map( wrapperEl, {
                            scrollWheelZoom: false, // disable original zoom function for smooth wheel zoom
                            center,
                            zoom,
                            zoomControl    : false, // disable original to add one with fullscreen option
                            zoomAnimation  : true,
                            fadeAnimation  : true,
                            zoomSnap       : 0.25,
                            zoomDelta      : 0.25,
                            layers         : [ layers[ layerType ] ]
                        } )
                        .on( 'zoomend', onZoomEnd )
                        .on( 'moveend', onMoveEnd )
                        .on( 'baselayerchange', onBaseLayerChange )
                        .on( 'layeradd', addEventsToMarkers )
                        .on( 'overlayadd', onOverlayAdd )
                        .on( 'overlayremove', onOverlayRemove )
                        .on( 'click', onMapClick );

                    // Enable smooth wheel zoom after map is fully initialized
                    ( map.current as any ).smoothWheelZoom.enable();

                    layerControl.current = L.control.layers( maps ).addTo( map.current );

                    // add custom zoom
                    const CustomZoomControl = L.Control.extend( {
                              options: {
                                  position           : 'topleft',
                                  zoomInText         : '<span aria-hidden="true">+</span>',
                                  zoomInTitle        : 'Zoom in',
                                  zoomOutText        : '<span aria-hidden="true">&#x2212;</span>',
                                  zoomOutTitle       : 'Zoom out',
                                  zoomFullscreenText : `<span id="${elementId}_zoomCtrl_fullscreen" class="yn-icon material-symbols-outlined">fullscreen</span>`,
                                  zoomFullscreenTitle: 'Fullscreen'
                              },

                              onAdd: function( zmap : L.Map ) {
                                  const controlName = 'leaflet-control-zoom',
                                        container   = L.DomUtil.create( 'div', controlName + ' leaflet-bar' ),
                                        options     = this.options;

                                  this._zoomInButton = this._createButton(
                                      options.zoomInText,
                                      options.zoomInTitle,
                                      controlName + '-in',
                                      container,
                                      this._zoomIn
                                  );
                                  this._zoomOutButton = this._createButton(
                                      options.zoomOutText,
                                      options.zoomOutTitle,
                                      controlName + '-out',
                                      container,
                                      this._zoomOut
                                  );
                                  this._zoomFullscreen = this._createButton(
                                      options.zoomFullscreenText,
                                      options.zoomFullscreenTitle,
                                      controlName + '-fullscreen',
                                      container,
                                      this._zoomFullscreen
                                  );

                                  this._updateDisabled();
                                  zmap.on( 'zoomend zoomlevelschange', this._updateDisabled, this );

                                  return container;
                              },

                              onRemove: function( zmap : L.Map ) {
                                  zmap.off( 'zoomend zoomlevelschange', this._updateDisabled, this );
                              },

                              _zoomIn: function( e : any ) {
                                  this._map.zoomIn( e.shiftKey ? 3 : 1 );
                              },

                              _zoomOut: function( e : any ) {
                                  this._map.zoomOut( e.shiftKey ? 3 : 1 );
                              },

                              _zoomFullscreen: function( e : any ) {
                                  const newFullScreen = !fullScreen.current,
                                        mapElem       = document.getElementById( elementId )?.parentElement;

                                  fullScreen.current = newFullScreen;

                                  if ( newFullScreen && mapElem ) {
                                      void mapElem.requestFullscreen();
                                  }
                                  else if ( document.fullscreenElement ) {
                                      void document.exitFullscreen();
                                  }
                              },

                              _createButton: function( html : string, title : string, className : string, container : HTMLDivElement, fn : DomEvent.EventHandlerFn ) {
                                  const link = L.DomUtil.create( 'a', className, container );
                                  link.innerHTML = html;
                                  link.href = '#';
                                  link.title = title;
                                  link.setAttribute( 'role', 'button' );
                                  link.setAttribute( 'aria-label', title );

                                  L.DomEvent.on( link, 'mousedown dblclick', L.DomEvent.stopPropagation )
                                      .on( link, 'click', L.DomEvent.stop )
                                      .on( link, 'click', fn, this )
                                      .on( link, 'click', this._refocusOnMap, this );

                                  return link;
                              },

                              _updateDisabled: function() {
                                  const zmap        = this._map as L.Map,
                                        currentZoom = zmap.getZoom(),
                                        className   = 'leaflet-disabled';

                                  L.DomUtil.removeClass( this._zoomInButton, className );
                                  L.DomUtil.removeClass( this._zoomOutButton, className );

                                  if ( currentZoom === zmap.getMinZoom() ) {
                                      L.DomUtil.addClass( this._zoomOutButton, className );
                                  }
                                  if ( currentZoom === zmap.getMaxZoom() ) {
                                      L.DomUtil.addClass( this._zoomInButton, className );
                                  }
                              }
                          } ),
                          customZoomControl = new CustomZoomControl();

                    customZoomControl.addTo( map.current );
                }
            }
        }
    );

    // update maps
    useEffect(
        () => {
            if ( map.current ) {
                layerControl.current?.remove();
                layerControl.current = L.control.layers( maps ).addTo( map.current );
            }
        },
        [
            maps
        ]
    );

    // update markers
    useEffect(
        () => {
            if ( map.current ) {
                let bounds : L.LatLngBounds | undefined;

                if ( markersLayers.current ) {
                    for ( const layer of markersLayers.current ) {
                        layerControl.current?.removeLayer( layer );
                        map.current.removeLayer( layer );
                    }
                }

                markersLayers.current = [];

                for ( const markerGroup of markers.current ?? [] ) {
                    const clusterGroup = new L.MarkerClusterGroup( {
                        maxClusterRadius       : 40,
                        disableClusteringAtZoom: 18,
                        showCoverageOnHover    : false,
                        spiderfyOnMaxZoom      : true,
                        zoomToBoundsOnClick    : true,
                        iconCreateFunction     : function( cluster ) {
                            const size = 10 + 40 * ( 1 - 1 / cluster.getChildCount() );

                            return L.divIcon( {
                                iconSize  : [ size, size ],
                                iconAnchor: [ size / 2, size / 2 ],
                                className : markerIconClassName,
                                html      : `<span><span class="yn-marker-border" style="background-color: ${markerGroup.color};"></span><span class="yn-marker-count" style="color: ${markerGroup.color}; font-weight: bold;">${cluster.getChildCount()}</span></span>`
                            } );
                        }
                    } );

                    for ( const marker of markerGroup.markers ) {
                        if ( !bounds ) {
                            bounds = L.latLngBounds( marker.position, marker.position );
                        }
                        else {
                            bounds.extend( marker.position );
                        }

                        clusterGroup.addLayer( L.marker(
                            marker.position,
                            {
                                icon: marker.icon
                                    ? L.divIcon( {
                                        iconSize  : [ 28, 28 ],
                                        iconAnchor: [ 14, 14 ],
                                        className : markerIconClassName,
                                        html      : marker.icon
                                    } )
                                    : undefined
                            }
                        ) );
                    }

                    markersLayers.current.push( L.layerGroup().addLayer( clusterGroup ) );
                    layerControl.current?.addOverlay( markersLayers.current[ markersLayers.current.length - 1 ], markerGroup.label );

                    if ( !overlays || !Object.keys( overlays ).includes( markerGroup.label ) || overlays[ markerGroup.label ] ) {
                        map.current.addLayer( markersLayers.current[ markersLayers.current.length - 1 ] );
                    }

                    if ( markerGroup.drawConnectionsByTime ) {
                        const label                                = `${markerGroup.label}_time`,
                              layer                                = L.layerGroup(),
                              markerPositionsWithTimeStampByRootId = markerGroup.markers.reduce(
                                  ( acc, m ) => {
                                      if ( !acc[ m.rootId ] ) {
                                          acc[ m.rootId ] = [];
                                      }

                                      acc[ m.rootId ].push( { position: m.position, timestamp: m.timestamp ?? '' } );

                                      return acc;
                                  },
                                  {} as { [ rootId : string ] : { position : [ number, number ]; timestamp : string; }[] }
                              );

                        for ( const markerPositionsWithTimeStamp of Object.values( markerPositionsWithTimeStampByRootId ) ) {
                            markerPositionsWithTimeStamp.sort( ( a, b ) => {
                                if ( a.timestamp > b.timestamp ) {
                                    return 1;
                                }
                                else if ( a.timestamp < b.timestamp ) {
                                    return -1;
                                }
                                else {
                                    return 0;
                                }
                            } );

                            // split by timestamp diff > 5 days (120 hours)
                            const timeStampDiffToSplit                                                                            = 120 * 60 * 60 * 1000,
                                  markerPositionsWithTimeStampChunks : { position : [ number, number ]; timestamp : string; }[][] = [];

                            let firstIndex    = 0,
                                lastTimeStamp = convertToDate( markerPositionsWithTimeStamp[ 0 ].timestamp )?.getTime();

                            if ( lastTimeStamp !== undefined ) {
                                for ( let index = 1; index < markerPositionsWithTimeStamp.length; index++ ) {
                                    const timeStamp = convertToDate( markerPositionsWithTimeStamp[ index ].timestamp )?.getTime();

                                    if ( timeStamp !== undefined ) {
                                        if ( timeStamp - lastTimeStamp > timeStampDiffToSplit ) {
                                            markerPositionsWithTimeStampChunks.push( markerPositionsWithTimeStamp.slice( firstIndex, index ) );
                                            firstIndex = index;
                                        }
                                    }
                                    else {
                                        break;
                                    }

                                    lastTimeStamp = timeStamp;
                                }
                            }
                            markerPositionsWithTimeStampChunks.push( markerPositionsWithTimeStamp.splice( firstIndex ) );

                            for ( const chunk of markerPositionsWithTimeStampChunks ) {
                                layer.addLayer( L.polyline(
                                    chunk.map( m => m.position ),
                                    {
                                        color  : markerGroup.color,
                                        opacity: 0.8,
                                        weight : 3,
                                        ...markerGroup.connectionStyle
                                    }
                                ) );
                            }
                        }

                        markersLayers.current.push( layer );

                        layerControl.current?.addOverlay( markersLayers.current[ markersLayers.current.length - 1 ], label );

                        if ( !overlays || !Object.keys( overlays ).includes( label ) || overlays[ label ] ) {
                            map.current.addLayer( markersLayers.current[ markersLayers.current.length - 1 ] );
                        }
                    }
                    else if ( markerGroup.drawConnectionsByConnect ) {
                        const label = `${markerGroup.label}_connections`,
                              layer = L.layerGroup();

                        for ( const marker of markerGroup.markers ) {
                            for ( const connectTo of marker.connectTo ?? [] ) {
                                layer.addLayer( L.polyline(
                                    [
                                        marker.position,
                                        connectTo
                                    ],
                                    {
                                        color  : markerGroup.color,
                                        opacity: 0.8,
                                        weight : 3,
                                        ...markerGroup.connectionStyle
                                    }
                                ) );
                            }
                        }

                        markersLayers.current.push( layer );

                        layerControl.current?.addOverlay( markersLayers.current[ markersLayers.current.length - 1 ], label );

                        if ( !overlays || !Object.keys( overlays ).includes( label ) || overlays[ label ] ) {
                            map.current.addLayer( markersLayers.current[ markersLayers.current.length - 1 ] );
                        }
                    }
                }

                if ( fitBounds && bounds && !initialized ) {
                    map.current.fitBounds( bounds );

                    if ( setFormPersistentSubPanelProps ) {
                        setFormPersistentSubPanelProps( 'initialized', true );
                    }

                    dispatch( setParam( uid, 'initialized', true ) );
                }

                addEventsToMarkers();
            }
        },
        [
            markerIconClassName,
            markers.current,
            map.current
        ]
    );

    return <>
        {mapElement}
        {tooltip}
    </>;
} );
Map.defaultProps = defaultProps;
Map.displayName = 'Map';

export default Map;
